// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — Skeletons de carregamento
//
// Um skeleton só ajuda quando tem a FORMA do que vai chegar. Bloco
// genérico no lugar de uma tabela não reduz a espera percebida: só troca
// "nada" por "nada cinza", e ainda produz salto de layout quando o
// conteúdo real entra com outra altura.
//
// Por isso aqui não existe um <Skeleton> universal solto: existe um por
// formato — texto, card, KPI, tabela, página.
//
// A animação de brilho corre sobre uma base sólida (classe .sk em
// theme/tokens.js). Com prefers-reduced-motion o brilho congela e sobra
// a base, que continua legível.
// ═══════════════════════════════════════════════════════════════════
import React from "react";
import { T } from "../../theme/tokens";

/**
 * Trabalho em andamento sem forma conhecida (importação, cálculo em segundo
 * plano). Substitui o emoji ⏳, que mudava de desenho por sistema (#218).
 * Rotação contínua é linear: keyframe mGira em theme/tokens.js.
 */
export function Girando({ size = 14 }) {
  return (
    <span aria-hidden="true" style={{
      width:size, height:size, flexShrink:0, borderRadius:"50%",
      border:`2px solid ${T.border2}`, borderTopColor:T.sub,
      animation:"mGira .8s linear infinite",
    }}/>
  );
}

/** Bloco base. Use os componentes de forma abaixo em vez deste, quando houver. */
export function Bloco({ l = "100%", a = 12, r = 6, style = {} }) {
  return <div className="sk" style={{ width:l, height:a, borderRadius:r, ...style }} />;
}

/**
 * Linhas de texto. A última sai mais curta de propósito: parágrafo real
 * não termina alinhado à direita, e o olho reconhece isso como texto.
 */
function SkeletonTexto({ linhas = 3, alturaLinha = 11, gap = 8, style = {} }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap, ...style }}>
      {Array.from({ length: linhas }, (_, i) => (
        <Bloco key={i} a={alturaLinha} l={i === linhas - 1 ? "62%" : "100%"} r={4} />
      ))}
    </div>
  );
}

/** Faixa de KPIs — o topo do Dashboard e da Análise. */
export function SkeletonKPI({ n = 4, style = {} }) {
  return (
    <div style={{
      display:"grid", gridTemplateColumns:`repeat(${n}, 1fr)`, gap:12, ...style,
    }}>
      {Array.from({ length: n }, (_, i) => (
        <div key={i} style={{
          background:T.card, border:`1px solid ${T.border}`,
          borderRadius:10, padding:"14px 16px",
        }}>
          <Bloco l="55%" a={9}  r={4} style={{ marginBottom:12 }} />
          <Bloco l="38%" a={22} r={5} />
        </div>
      ))}
    </div>
  );
}

/**
 * Tabela. `colunas` aceita número (larguras iguais) ou array de larguras
 * — passe o array quando as colunas reais forem desiguais, senão o
 * conteúdo entra deslocando tudo.
 */
export function SkeletonTabela({ linhas = 8, colunas = 6, comCabecalho = true, style = {} }) {
  const larg = Array.isArray(colunas)
    ? colunas
    : Array.from({ length: colunas }, () => `${100 / colunas}%`);

  const Linha = ({ alt, op }) => (
    <div style={{
      display:"flex", gap:14, alignItems:"center",
      padding:"10px 14px", opacity:op,
      borderBottom:`1px solid ${T.border}`,
    }}>
      {larg.map((l, i) => <Bloco key={i} l={l} a={alt} r={4} />)}
    </div>
  );

  return (
    <div style={{
      background:T.card, border:`1px solid ${T.border}`,
      borderRadius:10, overflow:"hidden", ...style,
    }}>
      {comCabecalho && <Linha alt={9} op={0.75} />}
      {/* As últimas linhas desbotam: sugere continuação, e evita a parede
          cinza que um bloco uniforme cria. */}
      {Array.from({ length: linhas }, (_, i) => (
        <Linha key={i} alt={11} op={1 - (i / linhas) * 0.55} />
      ))}
    </div>
  );
}

/**
 * Fallback do Suspense na troca de módulo. Genérico de propósito: o
 * Suspense não sabe qual tela vem, e um formato específico errado é pior
 * que um neutro.
 */
export function SkeletonPagina() {
  return (
    <div className="fu" style={{ display:"flex", flexDirection:"column", gap:18 }}>
      <div>
        <Bloco l={220} a={17} style={{ marginBottom:8 }} />
        <Bloco l={320} a={10} r={4} />
      </div>
      <SkeletonKPI n={4} />
      <SkeletonTabela linhas={7} colunas={6} />
    </div>
  );
}
